import { CardForm, InputEmailForm, InputTextForm } from '@/uiComponents/common';
import SelectForm from '@/uiComponents/common/selects/selectNew';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { getAffiliateType } from '@/helpers/concierge';
import { hasAllowedRoles } from '@/helpers/roles';
import { Label } from '@/components/ui/label';
import ChangeAffiliateType from './changeAffiliateType/changeAffiliateType';
import { getSalesCategorysNew } from '@/services';
import { useQuery } from 'react-query';
import { useContext } from 'react';
import { WelletContext } from '@/context/wellet/welletContext';
import { defaultQueryOptions } from '@/utils/defaultQueryOptions';

const JobInformation = ({ t, className, mode, id, refetchStatus }) => {
  const { t: translate } = useTranslation('translation', { keyPrefix: 'forms.userForm' });
  const { t: tGeneric } = useTranslation();
  const tenantId = useSelector((state) => state.app.tenantId);
  const organization = useSelector((state) => state.app.selectedOrganization);
  const welletContext = useContext(WelletContext);
  const isLaptop = window.innerWidth < 1430;
  const affiliateType = useWatch({ name: 'affiliateType' });
  const categoryId = useWatch({ name: 'categoryId' });
  const creationRoles = useWatch({ name: 'roles' });
  const methods = useFormContext();
  const canUpdateAffiliateType = hasAllowedRoles(creationRoles, [
    'bosssales',
    'salesteamlead',
    'associate',
  ]);

  const { data: allCategoriesData, isLoading } = useQuery({
    queryKey: ['allCategories', organization.marketId],
    queryFn: () => getSalesCategorysNew(welletContext, organization.marketId),
    ...defaultQueryOptions,
  });

  return (
    <>
      <CardForm title={t('jobInformation.title')} className={className}>
        <div className='row tw-mb-6'>
          {id ? (
            <div className='tw-space-y-2 col-6'>
              <Label htmlFor='email'>{t('rowAffiliateType.label')}</Label>
              <div className='tw-relative'>
                <div
                  className={`tw-flex tw-h-10 tw-w-full tw-items-center tw-rounded-md tw-border tw-border-input tw-bg-muted tw-px-3 tw-py-2 tw-text-sm ${
                    !canUpdateAffiliateType ? 'tw-cursor-not-allowed tw-opacity-50' : ''
                  }`}
                >
                  {getAffiliateType(affiliateType, tGeneric)}
                </div>
                {isLoading ? null : (
                  <ChangeAffiliateType
                    canEditAffiliate={canUpdateAffiliateType}
                    setValue={methods.setValue}
                    refetchStatus={refetchStatus}
                    categoryId={categoryId}
                    affiliateType={affiliateType}
                    allCategoriesData={allCategoriesData}
                  />
                )}
              </div>
            </div>
          ) : affiliateType || (!id && canUpdateAffiliateType) ? (
            <SelectForm
              options={[
                { label: getAffiliateType('CONCIERGE', tGeneric), value: 'CONCIERGE' },
                { label: getAffiliateType('INDEPENDENT', tGeneric), value: 'INDEPENDENT' },
                { label: getAffiliateType('INTERNAL_STAFF', tGeneric), value: 'INTERNAL_STAFF' },
              ]}
              label={t('rowAffiliateType.label')}
              placeholder=''
              className={'col-6 tw-mb-6'}
              name={'affiliateType'}
              isRequired={!id && canUpdateAffiliateType}
              isDisabled={!id ? false : true}
            />
          ) : null}
          <InputTextForm
            name={'sector'}
            className={'col-6 tw-mb-6'}
            label={t('rowLaboralData.inputCategory.label')}
            placeholder={t('rowLaboralData.inputCategory.placeholder')}
            maxLength={510}
          />
          <InputTextForm
            name={'company'}
            className={'col-6 tw-mb-6'}
            label={t('rowLaboralData.inputCompany.label')}
            placeholder={t('rowLaboralData.inputCompany.placeholder')}
            minLength={0}
            maxLength={510}
          />
          <InputTextForm
            name={'workUnit'}
            label={t('rowWorkUnit.label')}
            placeholder={t('rowWorkUnit.placeholder', {
              name:
                affiliateType === 'CONCIERGE'
                  ? tGeneric('generic.concierge')
                  : affiliateType === 'INTERNAL_STAFF'
                  ? tGeneric('generic.internal_staff')
                  : tGeneric('generic.affiliate'),
            })}
            className={'col-6 tw-mb-6'}
            isRequired={false}
            minLength={0}
            maxLength={510}
          />
          {process.env.REACT_APP_TENANT_GRN === tenantId && mode === 'CONCIERGE' ? (
            <>
              {' '}
              <InputTextForm
                className={'col-6 tw-mb-6'}
                name={'socialNetworkCompany'}
                label={translate('rowSocialNetworkCompany.label')}
                placeholder={translate('rowSocialNetworkCompany.placeholder')}
                maxLength={510}
              />
              <InputTextForm
                className={'col-6 tw-mb-6'}
                name={'webCompany'}
                label={translate('rowWebCompany.label')}
                placeholder={translate('rowWebCompany.placeholder')}
                maxLength={510}
              />
              <InputTextForm
                className={affiliateType ? 'col-6 tw-mb-6' : 'col-12 tw-mb-6'}
                name={'officeAddressCompany'}
                label={translate('rowOfficeAddressCompany.label')}
                placeholder={translate('rowOfficeAddressCompany.placeholder')}
                maxLength={510}
              />
              <InputEmailForm
                name={'corporateEmail'}
                label={translate('rowCorporateEmail.label')}
                placeholder={''}
                className={'col-6 tw-mb-6'}
                isRequired={false}
              />
            </>
          ) : null}
          <InputTextForm
            name={'jobTitle'}
            className={'col-6 tw-mb-6'}
            label={t('rowLaboralData.inputJob.label')}
            placeholder={t('rowLaboralData.inputJob.placeholder')}
            minLength={0}
            maxLength={510}
          />

          <InputTextForm
            className={'col-6'}
            minLength={0}
            maxLength={50}
            name={'internRef'}
            label={translate('inputReff.label')}
            placeholder={translate('inputReff.placeholder')}
            isRequired={false}
          />
          {process.env.REACT_APP_TENANT_GRN === tenantId && mode === 'CONCIERGE' ? (
            <>
              <SelectForm
                options={[
                  { label: translate('selectHasWorked.yes'), value: 'false' },
                  { label: translate('selectHasWorked.no'), value: 'true' },
                ]}
                label={translate('selectHasWorked.label', {
                  name: organization.marketName,
                })}
                placeholder=''
                className={isLaptop ? 'col-12 tw-mb-6' : 'col-6 tw-mb-6'}
                name={'hasWorked'}
                isDisabled={true}
              />

              <SelectForm
                options={[
                  { label: translate('selectHasFamiliar.yes'), value: 'false' },
                  { label: translate('selectHasFamiliar.no'), value: 'true' },
                ]}
                label={translate('selectHasFamiliar.label', {
                  name: organization.marketName,
                })}
                className={isLaptop ? 'col-12 tw-mb-6' : 'col-6 tw-mb-6'}
                name={'hasFamiliar'}
                placeholder=''
                isDisabled={true}
              />
            </>
          ) : null}

          {/* {mode === 'CONCIERGE' && affiliateType === 'CONCIERGE' ? (
            <>
              <div className='col-12 tw-pt-4 tw-border-t'>
                <div className='tw-flex tw-items-center tw-gap-3 tw-mb-2'>
                  <h3 className='tw-text-lg tw-font-semibold'>{t('workCredential.title')}</h3>
                  {getStatusBadge(workCredentialStatus, t)}
                </div>

                {workCredential?.length > 0 ? (
                  <Carousel>
                    <CarouselContent className='!tw-ml-0 !tw-gap-4'>
                      {workCredential?.map((w) => (
                        <CarouselItem key={w} className='!tw-basis-auto !tw-pl-0'>
                          <WorkCredentialImage
                            t={t}
                            style={{ width: '250px', height: '250px' }}
                            url={w}
                            name={name}
                          />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {workCredential?.length > 1 ? (
                      <>
                        <CarouselPrevious />
                        <CarouselNext />
                      </>
                    ) : null}
                  </Carousel>
                ) : (
                  <div>{t('workCredential.noImage')}</div>
                )}
              </div>
              <div className='tw-mt-3 col-12 tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-gap-3 tw-pt-2'>
                {workCredentialStatus !== 'PENDING' ? (
                  <p className='tw-text-sm tw-text-muted-foreground'>
                    {getStatusText(workCredentialStatus, t)}
                  </p>
                ) : null}
                <div className='tw-flex tw-gap-2'>
                  {workCredentialStatus !== 'APPROVED' && canApprove ? (
                    <Button
                      onClick={handleApproveClick}
                      size='sm'
                      type='button'
                      className='tw-bg-black hover:tw-bg-black/90'
                    >
                      <Check className='tw-w-4 tw-h-4 tw-mr-2' />
                      {t('approved.button')}
                    </Button>
                  ) : null}
                  {workCredentialStatus !== 'REJECTED' && canApprove ? (
                    <Button
                      onClick={handleRejectClick}
                      size='sm'
                      variant='outline'
                      type='button'
                      className='tw-bg-transparent'
                    >
                      <X className='tw-w-4 tw-h-4 tw-mr-2' />
                      {t('rejected.button')}
                    </Button>
                  ) : null}
                </div>
              </div>
              <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {pendingAction === 'approve'
                        ? t('approved.modal.title')
                        : t('rejected.modal.title')}
                    </DialogTitle>
                    <DialogDescription className='!tw-mt-3'>
                      <div className='tw-text-black'>
                        {pendingAction === 'approve'
                          ? t('approved.modal.text')
                          : t('rejected.modal.text')}
                      </div>
                      <div className='tw-mt-1 tw-text-muted-foreground'>
                        {pendingAction === 'approve'
                          ? t('approved.modal.action')
                          : t('rejected.modal.action')}
                      </div>
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant='outline'
                      type='button'
                      onClick={handleCancel}
                      disabled={!aprroveRejectMutation.isIdle}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      onClick={handleConfirm}
                      type='button'
                      disabled={!aprroveRejectMutation.isIdle}
                      className='tw-bg-black hover:tw-bg-black/90'
                    >
                      {t('confirm')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : null} */}
        </div>
        {/* {aprroveRejectMutation?.isSuccess ? (
          <Toast
            message={
              aprroveRejectMutation?.variables?.workCredentialStatus === 'APPROVED'
                ? t('approved.toast')
                : t('rejected.toast')
            }
            type={'SUCCESS'}
            onCallback={() => {
              aprroveRejectMutation.reset();
              setPendingAction(null);
              setIsModalOpen(false);
              refetchStatus();
            }}
          />
        ) : null}
        {aprroveRejectMutation?.isError ? (
          <Toast
            message={t('toast.error')}
            type={'ERROR'}
            onCallback={() => aprroveRejectMutation.reset()}
          />
        ) : null} */}
      </CardForm>
    </>
  );
};

export default JobInformation;
